import { useMemo } from 'react';
import type { FC, ReactNode } from 'react';

import type { Moment } from 'moment';
import { useIntl } from 'react-intl';

import { DatePicker, Input, Radio } from 'antd';

import Title from '@src/components/BoxContainer/components/Title';
import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import { ResponsiveTable } from '@src/components/Table';
import type { ResponsiveTableColumns } from '@src/components/Table';
import { formatAttendanceIdentifierDisplay } from '@src/packages/utils/nationalId';
import IntlMessages from '@src/util/IntlMessages';

import { formatCoverUserDisplayName } from '../../../formatCoverUserDisplayName';
import { CoverCurrentJourneyLabel } from '../../../CoverCurrentJourneyLabel';
import { WorkJourneyCoverConflictAlert } from '../../../WorkJourneyCoverConflictAlert';
import type {
  CoverReplaceModificationForm,
  CoverReplaceStep2Variant,
  CoverReplaceUserRow,
  TCoverReplaceDurationMode,
} from '../../types';
import {
  isOutsideCoverReplaceAllowedRange,
  type CoverReplaceAllowedRange,
} from '../../utils/resolveCoverReplaceAllowedRange';
import styles from '../../styles.module.less';

export type WorkJourneyCoverReplaceStep2Props = {
  variant: CoverReplaceStep2Variant;
  organizationCountry?: string | null;
  form: CoverReplaceModificationForm;
  replacedUserRow: CoverReplaceUserRow | null;
  replacementUserRow: CoverReplaceUserRow | null;
  alertTitle: ReactNode;
  alertDescription: ReactNode;
  /** Inclusive absence window; DatePickers disable days outside it. */
  allowedRange?: CoverReplaceAllowedRange | null;
  /** When false, hide permanent (e.g. range is clamped to an absence). */
  allowPermanent?: boolean;
  onDurationModeChange: (mode: TCoverReplaceDurationMode) => void;
  onStartDateChange: (value: Moment | null) => void;
  onEndDateChange: (value: Moment | null) => void;
  onCommentChange: (value: string) => void;
};

export const WorkJourneyCoverReplaceStep2: FC<WorkJourneyCoverReplaceStep2Props> = ({
  variant,
  organizationCountry,
  form,
  replacedUserRow,
  replacementUserRow,
  alertTitle,
  alertDescription,
  allowedRange = null,
  allowPermanent = true,
  onDurationModeChange,
  onStartDateChange,
  onEndDateChange,
  onCommentChange,
}) => {
  const intl = useIntl();
  const showPermanent = variant === 'replace' && allowPermanent;
  const showReplacedSection = variant === 'replace';
  const showEndDate = form.durationMode === 'range';
  const showStartDate = form.durationMode !== 'permanent';

  const isDateDisabled = (current: Moment | null) =>
    Boolean(current && isOutsideCoverReplaceAllowedRange(current, allowedRange));

  const userColumns: ResponsiveTableColumns<CoverReplaceUserRow> = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'workJourney.management.cover.columns.user' }),
        key: 'user',
        render: user => (
          <div className={styles.userCell}>
            <MemberAvatar size={32} user={user} />
            <span>{formatCoverUserDisplayName(user)}</span>
          </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'workJourney.management.cover.columns.idCard' }),
        key: 'idCard',
        render: user =>
          formatAttendanceIdentifierDisplay(organizationCountry, user) || '—',
      },
      {
        title: intl.formatMessage({
          id: 'workJourney.management.cover.replace.columns.currentJourney',
        }),
        key: 'currentJourney',
        render: user => (
          <CoverCurrentJourneyLabel
            journeyName={user.hasAssignedJourney ? user.currentJourneyLabel : null}
          />
        ),
      },
    ],
    [intl, organizationCountry],
  );

  return (
    <>
      <div className="gx-mt-4">
        <WorkJourneyCoverConflictAlert title={alertTitle} description={alertDescription} />
      </div>

      <Title.SubTitle
        className="gx-mt-4 gx-mb-3"
        value={<IntlMessages id="workJourney.management.cover.replace.step2.modificationSection" />}
      />

      <Radio.Group
        className={styles.durationRadios}
        value={form.durationMode}
        onChange={event => onDurationModeChange(event.target.value)}
      >
        <Radio value="single">
          <IntlMessages id="workJourney.management.cover.replace.step2.duration.single" />
        </Radio>
        <Radio value="range">
          <IntlMessages id="workJourney.management.cover.replace.step2.duration.range" />
        </Radio>
        {showPermanent && (
          <Radio value="permanent">
            <IntlMessages id="workJourney.management.cover.replace.step2.duration.permanent" />
          </Radio>
        )}
      </Radio.Group>

      {showStartDate && (
        <div className={styles.dateFields}>
          <div className={styles.dateField}>
            <label className={styles.dateLabel}>
              <IntlMessages id="workJourney.management.cover.replace.step2.startDate" />
              <span className={styles.required}>*</span>
            </label>
            <DatePicker
              className={styles.datePicker}
              format="DD/MM/YYYY"
              value={form.startDate}
              onChange={onStartDateChange}
              disabledDate={isDateDisabled}
              data-testid="cover-replace-start-date"
            />
          </div>
          {showEndDate && (
            <div className={styles.dateField}>
              <label className={styles.dateLabel}>
                <IntlMessages id="workJourney.management.cover.replace.step2.endDate" />
                <span className={styles.required}>*</span>
              </label>
              <DatePicker
                className={styles.datePicker}
                format="DD/MM/YYYY"
                value={form.endDate}
                onChange={onEndDateChange}
                disabledDate={current =>
                  isDateDisabled(current) ||
                  Boolean(form.startDate && current && current.isBefore(form.startDate, 'day'))
                }
                data-testid="cover-replace-end-date"
              />
            </div>
          )}
        </div>
      )}

      <div className={styles.commentField}>
        <Input.TextArea
          rows={4}
          value={form.comment}
          placeholder={intl.formatMessage({
            id: 'workJourney.management.cover.replace.step2.commentPlaceholder',
          })}
          onChange={event => onCommentChange(event.target.value)}
          data-testid="cover-replace-comment"
        />
      </div>

      {showReplacedSection && replacedUserRow && (
        <>
          <Title.SubTitle
            className="gx-mt-4 gx-mb-3"
            value={
              <IntlMessages id="workJourney.management.cover.replace.step2.replacedUserSection" />
            }
          />
          <ResponsiveTable dataSource={[replacedUserRow]} columns={userColumns} />
        </>
      )}

      {replacementUserRow && (
        <>
          <Title.SubTitle
            className="gx-mt-4 gx-mb-3"
            value={
              <IntlMessages
                id={
                  variant === 'assign'
                    ? 'workJourney.management.cover.replace.step2.assignedUserSection'
                    : 'workJourney.management.cover.replace.step2.replacementUserSection'
                }
              />
            }
          />
          <ResponsiveTable dataSource={[replacementUserRow]} columns={userColumns} />
        </>
      )}
    </>
  );
};
